import React, { useState, useEffect, useMemo } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { PaymentDetailType, STATE_VIEW } from "./props"
import { getLogoApp } from "src/utils/app-logo"
import {getPaymentToken, getCloverPublicKey, chargeClover, gatewayCharge} from "src/services/payment-service"
import { handlePromiseAll } from "src/utils/common"
import { LoadingView } from "../order-cart/loading-view"
import { useTranslation } from "react-i18next"
import "./index.scss"
import { CloverInstance } from "src/utils/cloverInstance"
import { customValueByUS } from "src/utils/currency-utils"
import { useToast } from "src/hooks/use-toast"

interface CardTokenInfoType {
  token: string
  isGet: boolean
}
export default function CloveCheckout() {
  const [isNoHeader, setIsNoHeader] = useState<boolean>(false)
  const [stateView, setStateView] = useState<string>(STATE_VIEW.INIT)
  const [searchParams] = useSearchParams()
  const navigator = useNavigate()
  const [paymentDetail, setPaymentDetail] = useState<PaymentDetailType | undefined> (undefined)
  const [isRender, setIsRender] = useState<boolean>(false)
  const [cardTokenInfo, setCardTokenInfo] = useState<CardTokenInfoType | undefined>(undefined)
  const [cardInfo, setCardInfo] = useState<string>("")
  const [isSubmit, setIsSubmit] = useState<boolean>(false)
  const {t} = useTranslation()
  const showToast = useToast()
  // init view
  useEffect(() => {
    validatePage()
  }, [])
  // RENDER VIEW
  useEffect(() => {
    if (isRender) {
      if(paymentDetail) setStateView(STATE_VIEW.RENDER)
    }
  }, [isRender, paymentDetail])

  const getPaymentTokenDetail = (token: string) => {
    return getPaymentToken(token)
      //eslint-disable-next-line @typescript-eslint/no-explicit-any
      .then((res: any) => {
        if (!res?.data?.data?.expired) {
          setPaymentDetail({
            failCallback: res.data.data?.fail_callback || "",
            expiredTime: (res.data.data?.expired_at || 0) * 1000,
            redirectUrl: res.data.data?.redirect_url || "",
            total: res.data.data?.orders?.length ? res.data.data.orders[0]?.total : 0,
            id: res.data.data?.orders?.length ? res.data.data.orders[0]?.code : 0,
          })
        } else {
          const failUrl = res.data.data?.fail_callback
          window.open(failUrl, "_self")
        }
      })
      .catch(() => {
        console.log("error")
      })
  }

  useEffect(() => {
    const result =  searchParams.get("noheader")
    const valueHeader = result && result === "true" ? true : false
    setIsNoHeader(valueHeader)
  }, [])

  const validatePage = () => {
    setStateView(STATE_VIEW.RENDER)
    const token = searchParams.get("token")
    const paymentType = searchParams.get("paymentType")
    if (!token || paymentType != "CLOVER") {
      navigator("/")
      return
    } else {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const apis = [] as any[]
      apis.push(getPaymentTokenDetail(token))
      apis.push(getKey())
      handlePromiseAll(apis).then(() => {
        setIsRender(true)
      })      
    }
  }



  const gotoHome = () => {
    const homeUrl = process.env.REACT_APP_HOME_DOMAIN_URL
    window.open(homeUrl, "_self")
  }

  const getKey = () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return getCloverPublicKey().then((res:any) => {
      if(res?.data?.data) {
        CloverInstance.init(res.data.data)
        const getCardInfo = (info: string) => {
          setCardInfo(info)
        }
        CloverInstance.bindingForm(getCardInfo)
      }
    }).catch(err => console.log(err))
  }
  const handleSubmit = () => {
    if(isSubmit) return
    setIsSubmit(true)
    const callBack = function (data:CardTokenInfoType) {
      setCardTokenInfo(data)
    }
    CloverInstance.getCardToken(callBack)
  }

  useEffect(() => {
    if(cardTokenInfo) {
      if(cardTokenInfo.isGet && cardTokenInfo.token) {
        const token = searchParams.get("token")
        const data = {
          paymentToken: token || "",
          cardToken: cardTokenInfo.token || "",
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        chargeClover(data).then((res: any) => {
          if(res?.data?.data?.status === "succeeded") {
            window.open(res?.data?.data?.success_callback, "_self")
          } else window.open(paymentDetail?.failCallback, "_self")
        }).catch(err => {
          const message = err?.response?.data?.error?.message || "Payment Fail"
          showToast({
            detail: message,
            severity: "error",
          })
        })
      }
    }
  }, [cardTokenInfo])

  const switchPayment = () => {
    const token = searchParams.get("token")
    gatewayCharge(token || "", "APP_OTA").finally(() => {
      navigator(`/ota-checkout?token=${token}&noheader=${isNoHeader ? "true": "false"}`)
    })
  }

  const getCardInfoStatus = useMemo(() => {
    const dataChange = JSON.parse(cardInfo|| "{}")
    const data = {
      CARD_NUMBER: {
        error: dataChange?.CARD_NUMBER?.error || "",
        touch: dataChange?.CARD_NUMBER?.touched || false,
      },
      CARD_DATE: {
        error: dataChange?.CARD_DATE?.error || "",
        touch: dataChange?.CARD_DATE?.touched || false,
      },
      CARD_CVV: {
        error: dataChange?.CARD_CVV?.error || "",
        touch: dataChange?.CARD_CVV?.touched || false,
      },
      CARD_POSTAL_CODE: {
        error: dataChange?.CARD_POSTAL_CODE?.error || "",
        touch: dataChange?.CARD_POSTAL_CODE?.touched || false,
      },
      isValid: !dataChange?.CARD_POSTAL_CODE?.error && dataChange?.CARD_POSTAL_CODE?.touched &&
              !dataChange?.CARD_CVV?.error && dataChange?.CARD_CVV?.touched &&
              !dataChange?.CARD_DATE?.error && dataChange?.CARD_DATE?.touched &&
              !dataChange?.CARD_NUMBER?.error && dataChange?.CARD_NUMBER?.touched,
    }
    return data
  }, [cardInfo])

  return (
    <div id="clover-checkout-view" className="min-h-[700] h-[auto] w-full relative w-screen">
      {
        stateView !== STATE_VIEW.RENDER ? (
          <div className="bg-white w-full absolute h-screen top-[0px] left-[0px] ">
            <LoadingView />
          </div>
        ) : (
          <div className="flex flex-col w-full h-full top-[0px] left-[0px] ">
            {/* HEADER */}
            <div id="cloverHeader" className="max-w-[1010px] w-full mx-auto mt-[12px]">
              <div className="mx-auto flex gap-1 md:flex-row h-full max-w-[1010px] px-[12px] md:px-[0px] md:px-[0px] items-center justify-between">
                <div
                  className={`cursor-pointer items-center flex ${isNoHeader ? "invisible" : ""}`}
                  onClick={gotoHome}
                >
                  <img
                    alt="appLogo"
                    className="h-[20px] w-[20px] md:h-[38px] md:w-[38px]"
                    src={getLogoApp()}
                  />
                  <span className="ml-[10px] text-[20px] font-bold leading-[24px] text-[#1570EF] md:text-[32px] md:leading-[38px]">
                    {process.env.REACT_APP_TITLE}
                  </span>
                </div>
                 
                <div onClick={switchPayment}  className="text-[12px] leading-[16px] md:text-[16px] md:leading-[24px] cursor-pointer flex w-fit items-center justify-center border border-blue-200 text-blue-600 bg-blue-50 rounded-3 px-[8px] py-[12px]">
                  <span>{t("external-payment")}</span>
                </div>           
              </div>
            </div>
            <div className="h-[1px] w-screen mt-1 bg-gray-200 border border-gray-200" />
            {/* CONTENT */}
            <div id="cloverContent" className="max-w-[1000px] mx-auto flex flex-col-reverse md:flex-row mt-[24px] gap-[24px]">
              {/* PAYMENT FORM */}
              <div className="flex px-[24px] py-[20px] bg-white border border-gray-200 rounded-3 flex-col gap-2 max-w-[1000px] mx-auto w-screen h-auto" id="cloverCheckoutForm">
                <form action="/charge" method="post" className="w-full flex flex-col gap-[8px] text-gray-900" id="payment-form">
                  <span className="text-[18px] leading-[28px] text-gray-900 font-semibold">Payment method</span>
                  <div className="hidden">
                    <div id="amount" className="field card-number">
                      <input readOnly name="amount" placeholder="Amount" value={paymentDetail?.total || 0}/>
                    </div>
                  </div>
                  <div className="form-row">
                    <span>{"Card information"}</span>
                    <div id="card-number" className="field card-number"></div>
                    {
                      getCardInfoStatus.CARD_NUMBER.touch && getCardInfoStatus.CARD_NUMBER.error ? <span className="text-red-500">{getCardInfoStatus.CARD_NUMBER.error}</span> : null
                    }
                  </div>
    
                  <div className="flex flex-col md:flex-row gap-1 w-full">
                    <div className="form-row flex-1">
                      <span>{"Expired date"}</span>
                      <div id="card-date" className="field third-width"></div>
                      {
                        getCardInfoStatus.CARD_DATE.touch && getCardInfoStatus.CARD_DATE.error ? <span className="text-red-500">{getCardInfoStatus.CARD_DATE.error}</span> : null
                      }
                    </div>
                    <div className="form-row flex-1">
                      <span>{"CVC"}</span>
                      <div id="card-cvv" className="field third-width"></div>
                      {
                        getCardInfoStatus.CARD_CVV.touch && getCardInfoStatus.CARD_CVV.error ? <span className="text-red-500">{getCardInfoStatus.CARD_CVV.error}</span> : null
                      }
                    </div>
                  </div>              
    
                  <div className="form-row">
                    <span>{"ZIP CODE"}</span>
                    <div id="card-postal-code" className="field third-width"></div>
                    {
                      getCardInfoStatus.CARD_POSTAL_CODE.touch && getCardInfoStatus.CARD_POSTAL_CODE.error ? <span className="text-red-500">{getCardInfoStatus.CARD_POSTAL_CODE.error}</span> : null
                    }
                  </div>
                </form>
                <div className="button-container">
                  <button 
                    disabled={!getCardInfoStatus.isValid} 
                    onClick={handleSubmit}  
                    className={`${isSubmit ? "animate-pulse duration-75" : ""} ${!getCardInfoStatus.isValid ? "opacity-[0.5]" : ""} w-full px-[12px] py-[8px] bg-blue-600 text-white text-[16px] leading-[24px] rounded-3`}>{!isSubmit ? "Pay now" : "Loading"}</button>
                </div>
              </div>
              {/* PAYMENT DETAIL */}
              <div className="border bg-white border-gray-200 h-fit w-full md:w-[350px] flex flex-col bg-white rounded-3 px-[24px] py-[20px] gap-[12px]">
                <span className="text-[16px] leading-[24px] font-medium text-gray-900">{`Order #${paymentDetail?.id || ""}`}</span>
                <span className="text-red-600 text-[24px] leading-[32px] font-semibold">{customValueByUS(paymentDetail?.total || 0)}</span>
              </div>
            </div>
          </div>    
        )
      }  
    </div>
  )
}